import React from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { GetApp, Fullscreen, FullscreenExit } from "@material-ui/icons";
import { AiLaunchButton } from "../../ai/AiLaunchButton";
import { useHistory } from "react-router-dom";
import { useQuestion, useQuestionUsage, useUpdateQuestion, useDeleteQuestion, useCreateQuestion } from "../../queries";
import { ResponseOption, primaryText, emptyLocalizedText, RangeLabelColor } from "../../types/common";
import { Question, QuestionResponseType } from "../../types/common";
import { TranslatedTextField } from "shared/localizedText";
import {
  downloadJson,
  isValidSemanticKey,
  normalizeInternalName,
  INTERNAL_NAME_HINT,
  variantsToEditorTree,
  editorTreeToVariants,
  emptyEditorVariant,
  emptyEditorChild,
  structurallyEqual,
  errorMessage,
  type EditorVariant,
} from "./editorUtils";
import { editorStyles as styles } from "./editorStyles";
import { RESPONSE_TYPE_ENTRIES } from "../../responseTypes";

const QuestionEditor: React.VFC<{
  id: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDeleted?: () => void;
  onUnsavedChange?: (id: number, dirty: boolean) => void;
}> = ({ id, isExpanded, onToggleExpand, onDeleted, onUnsavedChange }) => {
  const history = useHistory();
  const { data: question, isLoading, error } = useQuestion(id);
  const { data: usages, isLoading: isLoadingUsages } = useQuestionUsage(id);
  const updateQuestion = useUpdateQuestion();
  const deleteQuestion = useDeleteQuestion();
  const createQuestion = useCreateQuestion();
  const [editedQuestion, setEditedQuestion] = React.useState<Question | null>(null);
  const savedQuestion = React.useRef<Question | null>(null);
  const editedQuestionRef = React.useRef<Question | null>(null);
  editedQuestionRef.current = editedQuestion;

  React.useEffect(() => {
    if (!question) return;
    const current = editedQuestionRef.current;
    const saved = savedQuestion.current;
    if (current && saved && !structurallyEqual(current, saved)) return;
    const q = { ...question };
    if (q.response_type?._type === "number-range") {
      const rt = q.response_type;
      q.response_type = {
        ...rt,
        min: { ...rt.min, color: rt.min.color ?? "negative" },
        max: { ...rt.max, color: rt.max.color ?? "positive" },
      };
    }
    setEditedQuestion(q);
    savedQuestion.current = q;
  }, [question]);

  const hasUnsavedChanges = !!(
    editedQuestion &&
    savedQuestion.current &&
    !structurallyEqual(editedQuestion, savedQuestion.current)
  );

  React.useEffect(() => {
    onUnsavedChange?.(id, hasUnsavedChanges);
  }, [id, hasUnsavedChanges, onUnsavedChange]);
  React.useEffect(() => () => onUnsavedChange?.(id, false), [id, onUnsavedChange]);

  if (isLoading) return <CircularProgress />;
  if (error) return <Typography style={{ color: "#f44336" }}>Failed to load question</Typography>;
  if (!editedQuestion) return null;

  const handleSave = () => {
    const sk = editedQuestion.semantic_key ?? "";
    if (sk && !isValidSemanticKey(sk)) {
      const normalized = normalizeInternalName(sk);
      if (!window.confirm(`"${sk}" is not a valid internal name. Save as "${normalized}"?`)) return;
      const updated = { ...editedQuestion, semantic_key: normalized };
      setEditedQuestion(updated);
      updateQuestion.mutate(
        { id, question: updated },
        {
          onSuccess: () => {
            savedQuestion.current = updated;
          },
        }
      );
      return;
    }
    const snapshot = editedQuestion;
    updateQuestion.mutate(
      { id, question: snapshot },
      {
        onSuccess: () => {
          savedQuestion.current = snapshot;
        },
      }
    );
  };

  const handleChange = (field: keyof Question, value: unknown) => {
    setEditedQuestion(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleResponseTypeChange = (field: string, value: unknown) => {
    setEditedQuestion(prev =>
      prev?.response_type
        ? {
            ...prev,
            response_type: { ...prev.response_type, [field]: value } as QuestionResponseType,
          }
        : prev
    );
  };

  const handleResponseTypeSwitch = (newType: string) => {
    const defaults: Record<string, QuestionResponseType> = {
      select: { _type: "select", variants: [] },
      "select-or-other": { _type: "select-or-other", variants: [] },
      "multi-select": { _type: "multi-select", variants: [] },
      "multi-select-grouped": { _type: "multi-select-grouped", groups: [] },
      checkbox: { _type: "checkbox" },
      integer: { _type: "integer", min: 0, max: 100 },
      "number-range": {
        _type: "number-range",
        min: { value: 0, color: "negative" as const },
        max: { value: 10, color: "positive" as const },
      },
      vas: { _type: "vas", min: 0, max: 100 },
      date: { _type: "date" },
      text: { _type: "text", multiline: false },
      "text-area": { _type: "text-area" },
      "multi-select-or-other": { _type: "multi-select-or-other", variants: [] },
    };

    setEditedQuestion(prev =>
      prev
        ? {
            ...prev,
            response_type: defaults[newType] ?? prev.response_type,
          }
        : prev
    );
  };

  const handleDownload = () => {
    if (editedQuestion) {
      downloadJson(editedQuestion, `${editedQuestion.id}.json`);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this question?`)) {
      deleteQuestion.mutate(id, {
        onSuccess: () => onDeleted?.(),
      });
    }
  };

  const handleDuplicate = () => {
    if (!question) return;
    createQuestion.mutate(undefined, {
      onSuccess: data => {
        const copy: Question = {
          ...question,
          id: data.id,
          semantic_key: undefined,
          question: { ...question.question, "en-us": (question.question?.["en-us"] ?? "") + " (copy)" },
        };
        updateQuestion.mutate(
          { id: data.id, question: copy },
          {
            onSuccess: () => history.push(`/health-forms/questions/${data.id}`),
          }
        );
      },
    });
  };

  return (
    <div className={styles.editorContainer}>
      <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
        <span />
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <AiLaunchButton
            context={{
              kind: "question",
              id,
              label: editedQuestion?.question?.["en-us"] || editedQuestion?.semantic_key || `Question #${id}`,
            }}
            tooltip="Edit this question with AI"
          />
          <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
            <IconButton size="small" onClick={onToggleExpand}>
              {isExpanded ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <div className={styles.form}>
        <Box className={styles.toolbar} display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
          <Button size="small" style={{ color: "#f44336" }} onClick={handleDelete} disabled={deleteQuestion.isLoading}>
            {deleteQuestion.isLoading ? "Deleting..." : "Delete"}
          </Button>
          <Button size="small" onClick={handleDuplicate} disabled={createQuestion.isLoading}>
            {createQuestion.isLoading ? "Duplicating..." : "Duplicate"}
          </Button>
          {hasUnsavedChanges && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => {
                if (savedQuestion.current) setEditedQuestion(savedQuestion.current);
              }}
            >
              Revert
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleSave}
            disabled={updateQuestion.isLoading}
          >
            {updateQuestion.isLoading ? "Saving..." : hasUnsavedChanges ? "Save*" : "Save"}
          </Button>
          <Tooltip title="Download JSON">
            <IconButton size="small" onClick={handleDownload}>
              <GetApp />
            </IconButton>
          </Tooltip>
        </Box>

        <TranslatedTextField
          label="Question Text"
          value={editedQuestion.question ?? emptyLocalizedText()}
          onChange={value => handleChange("question", value)}
          multiline
          rows={2}
        />

        <TranslatedTextField
          label="Description"
          value={editedQuestion.description ?? emptyLocalizedText()}
          onChange={value => handleChange("description", primaryText(value) ? value : undefined)}
          multiline
          rows={2}
        />

        <TextField
          label="Semantic Key"
          value={editedQuestion.semantic_key ?? ""}
          onChange={e => handleChange("semantic_key", e.target.value.trim() || undefined)}
          fullWidth
          variant="outlined"
          margin="normal"
          error={!isValidSemanticKey(editedQuestion.semantic_key)}
          helperText={!isValidSemanticKey(editedQuestion.semantic_key) ? INTERNAL_NAME_HINT : undefined}
        />

        <FormControl fullWidth variant="outlined" margin="normal">
          <InputLabel>Response Type</InputLabel>
          <Select
            value={editedQuestion.response_type?._type ?? ""}
            label="Response Type"
            onChange={e => handleResponseTypeSwitch(e.target.value as string)}
          >
            {RESPONSE_TYPE_ENTRIES.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {editedQuestion.response_type?._type === "multi-select-grouped" &&
          (() => {
            const responseType = editedQuestion.response_type;
            return (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Groups
                </Typography>
                {responseType.groups.map((group, groupIdx) => (
                  <Box key={groupIdx} mb={3} p={2} border="1px solid #e0e0e0" borderRadius={1}>
                    <Box display="flex" alignItems="flex-start" mb={2} style={{ gap: 8 }}>
                      <Box flex={1}>
                        <TranslatedTextField
                          label="Group Label"
                          value={group.label}
                          onChange={value => {
                            const groups = [...responseType.groups];
                            groups[groupIdx] = { ...groups[groupIdx], label: value };
                            handleResponseTypeChange("groups", groups);
                          }}
                        />
                      </Box>
                      <Button
                        size="small"
                        style={{ color: "#f44336", marginTop: 24 }}
                        onClick={() => {
                          const groups = responseType.groups.filter((_, i) => i !== groupIdx);
                          handleResponseTypeChange("groups", groups);
                        }}
                      >
                        ✕
                      </Button>
                    </Box>
                    <Typography variant="caption" display="block" gutterBottom>
                      Variants
                    </Typography>
                    {group.variants.map((variant, variantIdx) => (
                      <Box key={variantIdx} mb={1} p={1} border="1px solid #eee" borderRadius={1}>
                        <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                          <Box flex={1}>
                            <TranslatedTextField
                              label={`Variant ${variantIdx + 1}`}
                              value={variant.text}
                              onChange={value => {
                                const groups = [...responseType.groups];
                                const newVariants = [...groups[groupIdx].variants];
                                newVariants[variantIdx] = { ...newVariants[variantIdx], text: value };
                                groups[groupIdx] = { ...groups[groupIdx], variants: newVariants };
                                handleResponseTypeChange("groups", groups);
                              }}
                            />
                            <TextField
                              label="Semantic Key"
                              value={variant.semantic_key ?? ""}
                              onChange={e => {
                                const groups = [...responseType.groups];
                                const newVariants = [...groups[groupIdx].variants];
                                newVariants[variantIdx] = {
                                  ...newVariants[variantIdx],
                                  semantic_key: e.target.value.trim() || undefined,
                                };
                                groups[groupIdx] = { ...groups[groupIdx], variants: newVariants };
                                handleResponseTypeChange("groups", groups);
                              }}
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              size="small"
                              error={!isValidSemanticKey(variant.semantic_key)}
                              helperText={!isValidSemanticKey(variant.semantic_key) ? INTERNAL_NAME_HINT : undefined}
                            />
                          </Box>
                          <Button
                            size="small"
                            style={{ color: "#f44336", marginTop: 24 }}
                            onClick={() => {
                              const groups = [...responseType.groups];
                              const newVariants = groups[groupIdx].variants.filter(
                                (_: ResponseOption, i: number) => i !== variantIdx
                              );
                              groups[groupIdx] = { ...groups[groupIdx], variants: newVariants };
                              handleResponseTypeChange("groups", groups);
                            }}
                          >
                            ✕
                          </Button>
                        </Box>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      onClick={() => {
                        const groups = [...responseType.groups];
                        groups[groupIdx] = {
                          ...groups[groupIdx],
                          variants: [
                            ...groups[groupIdx].variants,
                            { id: 0, feel_id: "", text: emptyLocalizedText(), parent_label: null },
                          ],
                        };
                        handleResponseTypeChange("groups", groups);
                      }}
                    >
                      Add Variant
                    </Button>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => {
                    const groups = [...responseType.groups, { label: emptyLocalizedText(), variants: [] }];
                    handleResponseTypeChange("groups", groups);
                  }}
                >
                  Add Group
                </Button>
              </Box>
            );
          })()}

        {(editedQuestion.response_type?._type === "select" ||
          editedQuestion.response_type?._type === "select-or-other" ||
          editedQuestion.response_type?._type === "multi-select" ||
          editedQuestion.response_type?._type === "multi-select-or-other") &&
          (() => {
            const responseType = editedQuestion.response_type;
            const tree = variantsToEditorTree(responseType.variants);
            const updateTree = (newTree: EditorVariant[]) =>
              handleResponseTypeChange("variants", editorTreeToVariants(newTree));
            return (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Variants
                </Typography>
                {tree.map((node, idx) => (
                  <Box key={idx} mb={1} p={1} border="1px solid #eee" borderRadius={1}>
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                      <Box flex={1}>
                        <TranslatedTextField
                          label={node.children ? `Parent ${idx + 1}` : `Variant ${idx + 1}`}
                          value={node.label}
                          onChange={value => {
                            const newTree = [...tree];
                            newTree[idx] = { ...newTree[idx], label: value };
                            updateTree(newTree);
                          }}
                        />
                        {!node.children && (
                          <TextField
                            label="Semantic Key"
                            value={node.semanticKey ?? ""}
                            onChange={e => {
                              const newTree = [...tree];
                              newTree[idx] = { ...newTree[idx], semanticKey: e.target.value.trim() || undefined };
                              updateTree(newTree);
                            }}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            size="small"
                            error={!isValidSemanticKey(node.semanticKey)}
                            helperText={!isValidSemanticKey(node.semanticKey) ? INTERNAL_NAME_HINT : undefined}
                          />
                        )}
                      </Box>
                      <Button
                        size="small"
                        style={{ color: "#f44336", marginTop: 24 }}
                        onClick={() => updateTree(tree.filter((_, i) => i !== idx))}
                      >
                        ✕
                      </Button>
                    </Box>
                    {node.children?.map((child, childIdx) => (
                      <Box key={childIdx} display="flex" alignItems="flex-start" ml={3} mt={1} style={{ gap: 8 }}>
                        <Box flex={1}>
                          <TranslatedTextField
                            label={`Sub-variant ${childIdx + 1}`}
                            value={child.text}
                            onChange={value => {
                              const newTree = [...tree];
                              const newChildren = [...(newTree[idx].children ?? [])];
                              newChildren[childIdx] = { ...newChildren[childIdx], text: value };
                              newTree[idx] = { ...newTree[idx], children: newChildren };
                              updateTree(newTree);
                            }}
                          />
                          <TextField
                            label="Semantic Key"
                            value={child.semanticKey ?? ""}
                            onChange={e => {
                              const newTree = [...tree];
                              const newChildren = [...(newTree[idx].children ?? [])];
                              newChildren[childIdx] = {
                                ...newChildren[childIdx],
                                semanticKey: e.target.value.trim() || undefined,
                              };
                              newTree[idx] = { ...newTree[idx], children: newChildren };
                              updateTree(newTree);
                            }}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            size="small"
                            error={!isValidSemanticKey(child.semanticKey)}
                            helperText={!isValidSemanticKey(child.semanticKey) ? INTERNAL_NAME_HINT : undefined}
                          />
                        </Box>
                        <Button
                          size="small"
                          style={{ color: "#f44336", marginTop: 24 }}
                          onClick={() => {
                            const newTree = [...tree];
                            const newChildren = (newTree[idx].children ?? []).filter((_, i) => i !== childIdx);
                            newTree[idx] = newChildren.length
                              ? { ...newTree[idx], children: newChildren }
                              : { id: newTree[idx].id, feelId: newTree[idx].feelId, label: newTree[idx].label };
                            updateTree(newTree);
                          }}
                        >
                          ✕
                        </Button>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      style={{ marginLeft: 24, marginTop: 4 }}
                      onClick={() => {
                        const newTree = [...tree];
                        newTree[idx] = {
                          ...newTree[idx],
                          children: [...(newTree[idx].children ?? []), emptyEditorChild()],
                        };
                        updateTree(newTree);
                      }}
                    >
                      Add Sub-variant
                    </Button>
                  </Box>
                ))}
                <Button size="small" onClick={() => updateTree([...tree, emptyEditorVariant()])}>
                  Add Variant
                </Button>
              </Box>
            );
          })()}

        {(editedQuestion.response_type?._type === "integer" || editedQuestion.response_type?._type === "vas") &&
          (() => {
            const rt = editedQuestion.response_type;
            const parseIntOr = (raw: string, fallback: number) => {
              const v = parseInt(raw, 10);
              return Number.isFinite(v) ? v : fallback;
            };
            return (
              <Box display="flex" style={{ gap: 16 }}>
                <TextField
                  label="Min"
                  type="number"
                  value={rt.min}
                  onChange={e => handleResponseTypeChange("min", parseIntOr(e.target.value, rt.min))}
                  variant="outlined"
                  margin="normal"
                  disabled={rt._type === "vas"}
                />
                <TextField
                  label="Max"
                  type="number"
                  value={rt.max}
                  onChange={e => handleResponseTypeChange("max", parseIntOr(e.target.value, rt.max))}
                  variant="outlined"
                  margin="normal"
                  disabled={rt._type === "vas"}
                />
              </Box>
            );
          })()}

        {editedQuestion.response_type?._type === "number-range" &&
          (() => {
            const rt = editedQuestion.response_type;
            const parseIntOr = (raw: string, fallback: number) => {
              const v = parseInt(raw, 10);
              return Number.isFinite(v) ? v : fallback;
            };
            const colorOptions: { value: RangeLabelColor; label: string }[] = [
              { value: "positive", label: "Positive (green)" },
              { value: "negative", label: "Negative (red)" },
              { value: "neutral", label: "Neutral (blue)" },
            ];
            return (
              <Box mt={2}>
                <Box display="flex" style={{ gap: 16 }}>
                  <TextField
                    label="Min"
                    type="number"
                    value={rt.min.value}
                    onChange={e =>
                      handleResponseTypeChange("min", { ...rt.min, value: parseIntOr(e.target.value, rt.min.value) })
                    }
                    variant="outlined"
                    margin="normal"
                  />
                  <TextField
                    label="Max"
                    type="number"
                    value={rt.max.value}
                    onChange={e =>
                      handleResponseTypeChange("max", { ...rt.max, value: parseIntOr(e.target.value, rt.max.value) })
                    }
                    variant="outlined"
                    margin="normal"
                  />
                </Box>
                <Box display="flex" style={{ gap: 16 }}>
                  <FormControl variant="outlined" margin="normal" style={{ minWidth: 160 }}>
                    <InputLabel>Min Color</InputLabel>
                    <Select
                      value={rt.min.color ?? "negative"}
                      onChange={e =>
                        handleResponseTypeChange("min", { ...rt.min, color: e.target.value as RangeLabelColor })
                      }
                      label="Min Color"
                    >
                      {colorOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="outlined" margin="normal" style={{ minWidth: 160 }}>
                    <InputLabel>Max Color</InputLabel>
                    <Select
                      value={rt.max.color ?? "positive"}
                      onChange={e =>
                        handleResponseTypeChange("max", { ...rt.max, color: e.target.value as RangeLabelColor })
                      }
                      label="Max Color"
                    >
                      {colorOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <TranslatedTextField
                  label="Min Label"
                  value={rt.min.label ?? emptyLocalizedText()}
                  onChange={value =>
                    handleResponseTypeChange("min", { ...rt.min, label: primaryText(value) ? value : undefined })
                  }
                />
                <TranslatedTextField
                  label="Max Label"
                  value={rt.max.label ?? emptyLocalizedText()}
                  onChange={value =>
                    handleResponseTypeChange("max", { ...rt.max, label: primaryText(value) ? value : undefined })
                  }
                />
              </Box>
            );
          })()}

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary" display="block">
            ID: {editedQuestion.id}
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            FEEL ID: {editedQuestion.feel_id}
          </Typography>
          {editedQuestion.updated_at && (
            <Typography variant="caption" color="textSecondary" display="block">
              Updated: {new Date(editedQuestion.updated_at).toLocaleString()}
            </Typography>
          )}
          {editedQuestion.edited_by && (
            <Typography variant="caption" color="textSecondary" display="block">
              Edited by: {editedQuestion.edited_by}
            </Typography>
          )}
        </Box>

        <Divider style={{ marginTop: 24, marginBottom: 16 }} />

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Used in
          </Typography>
          {isLoadingUsages ? (
            <CircularProgress size={20} />
          ) : !usages?.length ? (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
              Not used in any questionnaires or question groups
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
              {usages.map(usage => (
                <Box
                  key={`${usage.type}-${usage.id}`}
                  className={styles.usageItem}
                  onClick={() => {
                    const path = usage.type === "questionnaire" ? "questionnaires" : "question-groups";
                    const anchor = usage.type === "questionnaire" ? `part-q-${id}` : `qg-qref-${id}`;
                    const params = new URLSearchParams();
                    params.set("highlight", `q:${id}`);
                    if (usage.page_position) params.set("page", String(usage.page_position));
                    history.push(`/health-forms/${path}/${usage.id}?${params}#${anchor}`);
                  }}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <span style={{ color: usage.is_direct ? "#28a745" : "#0066cc", fontSize: 16 }}>
                      {usage.is_direct ? "●" : "◎"}
                    </span>
                    <Typography variant="body2">{usage.name}</Typography>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {usage.id}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {updateQuestion.isError && (
          <Typography color="error" variant="caption" display="block" style={{ marginTop: 8 }}>
            Failed to save: {errorMessage(updateQuestion.error)}
          </Typography>
        )}
        {updateQuestion.isSuccess && (
          <Typography color="primary" variant="caption" display="block" style={{ marginTop: 8 }}>
            Saved successfully
          </Typography>
        )}
      </div>
    </div>
  );
};

export default QuestionEditor;
